import React, { useMemo } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { InfoCircle } from 'react-bootstrap-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatCurrency } from '../../../lib/utils';
import {
  computeRuleBreakCosts,
  RULE_BREAK_COST_EXPLANATIONS,
  filterBreachesByTimeRange,
} from '../../../utils/ruleBreakCosts';
import { getDashboardCardStyle, DASHBOARD_CARD_RADIUS } from '../../../constants/dashboardLayout';
import DashboardSectionTitle from '../../shared/DashboardSectionTitle';

function fmtSignedCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatCurrency(value)}`;
}

function valueColor(value, colors) {
  if (value === null || value === undefined || Number.isNaN(value)) return colors.textMuted;
  if (value > 0) return colors.success || '#10B981';
  if (value < 0) return colors.danger || '#EF4444';
  return colors.text;
}

/**
 * Account Health section: estimated P&L impact of breaking trading rules.
 */
const RuleBreakCostsSection = ({
  metrics = null,
  breaches = [],
  violations = [],
  timeRange = 'month',
  periodLabel = 'this period',
}) => {
  const { colors } = useTheme();

  const costs = useMemo(() => {
    const scopedBreaches = filterBreachesByTimeRange(breaches, timeRange);
    return computeRuleBreakCosts({ metrics, breaches: scopedBreaches, violations });
  }, [metrics, breaches, violations, timeRange]);

  const hasAnySignal =
    costs.no_stop_loss.available ||
    costs.daily_loss_unrecovered.available ||
    costs.max_trades_excess.available;

  const infoPopover = (
    <Popover id="rule-break-costs-info" style={{ maxWidth: 340 }}>
      <Popover.Header as="h3" style={{ fontSize: '0.8125rem', fontWeight: 600 }}>
        How we calculate this
      </Popover.Header>
      <Popover.Body style={{ fontSize: '0.75rem', lineHeight: 1.45, color: colors.text }}>
        <p style={{ marginBottom: 10 }}>{RULE_BREAK_COST_EXPLANATIONS.section}</p>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li style={{ marginBottom: 8 }}>
            <strong>No stop loss</strong> — {RULE_BREAK_COST_EXPLANATIONS.no_stop_loss}
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>Daily loss excess</strong> — {RULE_BREAK_COST_EXPLANATIONS.daily_loss_unrecovered}
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong>After max trades</strong> — {RULE_BREAK_COST_EXPLANATIONS.max_trades_excess}
          </li>
          <li>
            <strong>Total impact</strong> — {RULE_BREAK_COST_EXPLANATIONS.total_impact}
          </li>
        </ul>
      </Popover.Body>
    </Popover>
  );

  const rows = [
    {
      key: 'no_stop',
      label: 'No stop loss',
      primary: costs.no_stop_loss.total_pnl,
      secondary: costs.no_stop_loss.available
        ? `avg ${fmtSignedCurrency(costs.no_stop_loss.avg_pnl)} · ${costs.no_stop_loss.trade_count} trade${costs.no_stop_loss.trade_count === 1 ? '' : 's'}`
        : 'No closed no-stop trades in range',
      available: costs.no_stop_loss.available,
    },
    {
      key: 'daily_loss',
      label: 'Daily loss excess',
      primary: costs.daily_loss_unrecovered.total_excess_loss,
      secondary: costs.daily_loss_unrecovered.available
        ? `avg ${fmtSignedCurrency(costs.daily_loss_unrecovered.avg_excess_loss)} · ${costs.daily_loss_unrecovered.day_count} unrecovered day${costs.daily_loss_unrecovered.day_count === 1 ? '' : 's'}`
        : 'No unrecovered daily loss breaches',
      available: costs.daily_loss_unrecovered.available,
    },
    {
      key: 'max_trades',
      label: 'After max trades',
      primary: costs.max_trades_excess.avg_pnl,
      secondary: costs.max_trades_excess.available
        ? `total ${fmtSignedCurrency(costs.max_trades_excess.total_pnl)} · ${costs.max_trades_excess.trade_count} trade${costs.max_trades_excess.trade_count === 1 ? '' : 's'} across ${costs.max_trades_excess.day_count} day${costs.max_trades_excess.day_count === 1 ? '' : 's'}`
        : 'No excess trades after max-trades days',
      available: costs.max_trades_excess.available,
      primaryPrefix: costs.max_trades_excess.available ? 'avg ' : '',
    },
  ];

  return (
    <div style={{ marginTop: 36 }}>
      <DashboardSectionTitle title="What broken rules cost you" marginBottom={12}>
        <OverlayTrigger
          placement="bottom"
          trigger={['click', 'focus']}
          rootClose
          overlay={infoPopover}
        >
          <button
            type="button"
            aria-label="How rule-break costs are calculated"
            style={{
              background: 'none',
              border: 'none',
              padding: 4,
              margin: 0,
              cursor: 'pointer',
              color: colors.textMuted,
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1,
            }}
          >
            <InfoCircle size={15} />
          </button>
        </OverlayTrigger>
      </DashboardSectionTitle>

      <div
        style={{
          ...getDashboardCardStyle(colors, { radius: DASHBOARD_CARD_RADIUS }),
          padding: '16px 18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 14,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontSize: '0.6875rem',
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              Total impact · {periodLabel}
            </div>
            <div
              style={{
                fontSize: '1.5rem',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: valueColor(costs.total_impact, colors),
                lineHeight: 1.2,
              }}
            >
              {fmtSignedCurrency(costs.total_impact)}
            </div>
          </div>
          {!hasAnySignal && (
            <div style={{ fontSize: '0.8125rem', color: colors.textLight, maxWidth: 280 }}>
              No rule-break cost samples in {periodLabel} yet. Costs appear once closed trades and finalized breaches are available.
            </div>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          {rows.map((row) => (
            <div
              key={row.key}
              style={{
                backgroundColor: colors.bgSecondary,
                borderRadius: 8,
                padding: '12px 14px',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: '0.6875rem',
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                {row.label}
              </div>
              <div
                style={{
                  fontSize: '1.125rem',
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  color: row.available ? valueColor(row.primary, colors) : colors.textMuted,
                  lineHeight: 1.2,
                }}
              >
                {row.available
                  ? `${row.primaryPrefix || ''}${fmtSignedCurrency(row.primary)}`
                  : '—'}
              </div>
              <div
                style={{
                  fontSize: '0.6875rem',
                  color: colors.textMuted,
                  marginTop: 6,
                  lineHeight: 1.35,
                }}
              >
                {row.secondary}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RuleBreakCostsSection;
